import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from './api';
import type { EventStatus } from '../shared/event';

export function useEventStatus(authenticated: boolean, pollSeconds: number) {
  const [status, setStatus] = useState<EventStatus | null>(null);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const accept = useCallback((next: EventStatus) => {
    generation.current++;
    setStatus(next);
    setError('');
  }, []);
  useEffect(() => {
    let stopped = false;
    let fetching = false;
    const refresh = async () => {
      if (fetching || document.visibilityState === 'hidden') return;
      fetching = true;
      const version = generation.current;
      try {
        const next = await api.eventStatus();
        if (!stopped && version === generation.current) accept(next);
      } catch {
        if (!stopped && version === generation.current) {
          // Retain the last confirmed state during a transient network failure.
          // The server still enforces access on every upload.
          setError('We couldn’t check the event. Please check your connection; we’ll keep trying.');
        }
      } finally {
        fetching = false;
      }
    };
    void refresh();
    const timer = setInterval(() => void refresh(), pollSeconds * 1000);
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      stopped = true;
      clearInterval(timer);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [authenticated, pollSeconds, accept]);
  return { status, error, accept };
}
